import { Bar, Button, ButtonList, Justify, Position, Size, Theme } from '@busybusy/webapp-react-ui';
import classNames from 'classnames';
import { MoreButton } from 'components';
import { convertTimeEntryDataToCsv } from 'containers/timesheets/utils/utils';
import { useToastOpen } from 'contexts/ToastContext';
import useMultiEquipmentTrackingEnabled from 'hooks/settings/useMultiEquipmentTrackingEnabled/useMultiEquipmentTrackingEnabled';
import { DateTime } from 'luxon';
import { ReactNode } from 'react';
import { ClassName } from 'types/ClassName';
import ITimeRange from 'types/TimeRange';
import { useFeatureFlags } from 'utils/features';
import { downloadText } from 'utils/fileUtils';
import { t } from 'utils/localize';
import { ITimeEntryDataReportData } from '../TimeEntryDataReport/TimeEntryDataReport';
import './TimeEntryFilterBar.scss';

export interface ITimeEntryFilterBarProps {
  className?: ClassName;
  timeRange?: ITimeRange<DateTime>;
  data: ITimeEntryDataReportData[];
  loadedAll: boolean;
  // TODO: this type does not matter as the result isn't used. It should be refactored to be used.
  loadAll: () => Promise<any> | undefined;
  exportFileName: string;
  renderFilters: (forPrint: boolean) => ReactNode | undefined;
}

const TimeEntryFilterBar = (props: ITimeEntryFilterBarProps) => {
  const { className, timeRange, data, loadAll, loadedAll, exportFileName, renderFilters } = props;

  const classes = classNames('time-entry-filter-bar', className);
  const errorToast = useToastOpen();
  const isPro = useFeatureFlags('PRO');
  const hasMultiEquipmentTracking = useMultiEquipmentTrackingEnabled();

  function getTimeRangeFormat(timeRange: ITimeRange<DateTime>) {
    const today = DateTime.local();
    const sameYears = today.hasSame(timeRange.startTime, 'year') && today.hasSame(timeRange.endTime, 'year');
    return sameYears ? 'EEE, MMM d' : 'EEE, MMM d, yyyy';
  }

  const renderTimeRange = (timeRange: ITimeRange<DateTime>) => {
    const formattedStartTime = timeRange.startTime.toFormat(getTimeRangeFormat(timeRange));
    const formattedEndTime = timeRange.endTime.toFormat(getTimeRangeFormat(timeRange));

    if (formattedStartTime === formattedEndTime) {
      return <div className="time-range">{formattedStartTime}</div>;
    } else {
      return <div className="time-range">{formattedStartTime + ' - ' + formattedEndTime}</div>;
    }
  };

  function renderMoreContent(close: () => void) {
    const executeWithClose = (fn: () => void) => () => {
      fn();
      close();
    };

    return (
      <ButtonList>
        {isPro && <Button onClick={executeWithClose(exportReport)}>{t('Export')}</Button>}
        <Button onClick={executeWithClose(printReport)}>{t('Print')}</Button>
      </ButtonList>
    );
  }

  async function printReport() {
    if (!loadedAll) {
      await loadAll();
    }

    setTimeout(() => {
      window.print();
    }, 100);
  }

  async function exportReport() {
    // TODO: actually return data from load all and use it
    // This header is used in multiple spots and they don't return the
    // same data type even though data ends up the same
    if (!loadedAll) {
      await loadAll();
    }

    // TODO: temporary workaround to set time out to allow state to update.
    setTimeout(() => {
      if (data?.length !== 0) {
        const csv = convertTimeEntryDataToCsv(data, hasMultiEquipmentTracking);
        downloadText(csv, `${exportFileName}.csv`);
      } else {
        errorToast({ theme: Theme.DANGER, label: t('There is no data to export.') });
      }
    }, 250);
  }

  return (
    <div className={classes}>
      <Bar className="filter-bar mb-8 no-print" justify={Justify.SPACE_BETWEEN} size={Size.GROW}>
        <Bar justify={Justify.FLEX_START}>{renderFilters(false)}</Bar>
        <MoreButton
          className="mr-2"
          position={Position.BOTTOM_END}
          size={Size.MEDIUM}
          renderContent={renderMoreContent}
        />
      </Bar>
      <div className="print my-5">
        {renderFilters(true)}
        {timeRange && renderTimeRange(timeRange)}
      </div>
    </div>
  );
};

export default TimeEntryFilterBar;
